import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import { EmissionPointPicker } from '../../../components/bookkeeping/EmissionPointPicker.js';
import {
  type InvoiceLineFormValue,
  InvoiceLineRow,
} from '../../../components/bookkeeping/InvoiceLineRow.js';
import { SriStatusTrack } from '../../../components/bookkeeping/SriStatusTrack.js';
import type {
  Account,
  AccountNode,
  Customer,
  InvoiceLine,
  Invoice as InvoiceType,
  Item,
  TaxRate,
  Warehouse,
} from '../../../types/bookkeeping.js';
import type { ConfigStatus } from '../../../types/onboarding.js';

const FX_SCALE = 1_000_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

function defaultLine(): InvoiceLineFormValue {
  return {
    item_id: '',
    description: '',
    quantity: '1000',
    unit_price: '0',
    tax_rate_id: '',
    account_id: '',
    warehouse_id: '',
  };
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function InvoiceForm() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: invoiceId } = useParams<{ id: string }>();
  const isNew = !invoiceId;

  const today = new Date().toISOString().slice(0, 10);
  const [customerId, setCustomerId] = useState('');
  const [entryDate, setEntryDate] = useState(today);
  const [dueDate, setDueDate] = useState(addDays(today, 30));
  const [currency, setCurrency] = useState('');
  const [fxRate, setFxRate] = useState(FX_SCALE.toString());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<InvoiceLineFormValue[]>([defaultLine()]);
  const [seeded, setSeeded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emissionPointId, setEmissionPointId] = useState<string | null>(null);

  // Fetch config for base currency
  const { data: configData } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const baseCurrency = configData?.organization.baseCurrency ?? 'USD';

  // Auto-set currency once base currency is loaded (for new forms)
  useState(() => {
    if (!currency && baseCurrency !== 'USD') setCurrency(baseCurrency);
  });

  // Set currency default to baseCurrency once loaded (only if still empty)
  const effectiveCurrency = currency || baseCurrency;
  const sameCurrency = effectiveCurrency === baseCurrency || effectiveCurrency.length !== 3;

  // Auto-fetch fx rate
  const { data: fxData } = useQuery({
    queryKey: ['fx', effectiveCurrency, baseCurrency, entryDate],
    queryFn: () =>
      api<{ rate: string; source: string }>(
        `/bookkeeping/fx-rates?date=${entryDate}&from=${effectiveCurrency}&to=${baseCurrency}`,
      ),
    enabled: !!effectiveCurrency && effectiveCurrency.length === 3 && !sameCurrency,
    retry: false,
  });

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['bookkeeping-customers', activeOrgId],
    queryFn: () => api<{ customers: Customer[] }>('/bookkeeping/customers?limit=200'),
    enabled: !!activeOrgId,
  });
  const customers = (customersData?.customers ?? []).filter((c) => c.is_active);

  // Fetch items
  const { data: itemsData } = useQuery({
    queryKey: ['bookkeeping-items', activeOrgId],
    queryFn: () => api<{ items: Item[] }>('/bookkeeping/items?limit=200'),
    enabled: !!activeOrgId,
  });
  const items = (itemsData?.items ?? []).filter((i) => i.is_active);

  // Fetch tax rates
  const { data: taxRatesData } = useQuery({
    queryKey: ['bookkeeping-tax-rates', activeOrgId],
    queryFn: () => api<{ tax_rates: TaxRate[] }>('/bookkeeping/sales/tax-rates?limit=100'),
    enabled: !!activeOrgId,
  });
  const taxRates = (taxRatesData?.tax_rates ?? []).filter((r) => r.is_active);

  // Fetch accounts (revenue accounts)
  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const revenueAccounts = allAccounts.filter((a) => a.type === 'revenue' && a.is_active);

  // Fetch warehouses (for inventory line rows)
  const { data: warehousesData } = useQuery({
    queryKey: ['bookkeeping-warehouses', activeOrgId],
    queryFn: () => api<{ warehouses: Warehouse[] }>('/bookkeeping/inventory/warehouses'),
    enabled: !!activeOrgId,
  });
  const warehouses = (warehousesData?.warehouses ?? []).filter((w) => w.is_active);

  // Load existing invoice if editing
  const invoiceDetailQ = useQuery<{ invoice: InvoiceType; lines: InvoiceLine[] }>({
    queryKey: ['bk-invoice', invoiceId],
    queryFn: () =>
      api<{ invoice: InvoiceType; lines: InvoiceLine[] }>(
        `/bookkeeping/sales/invoices/${invoiceId}`,
      ),
    enabled: !isNew && !!invoiceId,
    onSuccess: (data: { invoice: InvoiceType; lines: InvoiceLine[] }) => {
      if (!seeded) {
        setCustomerId(data.invoice.customer_id);
        setEntryDate(data.invoice.entry_date);
        setDueDate(data.invoice.due_date);
        setCurrency(data.invoice.currency);
        setFxRate(data.invoice.fx_rate);
        setMemo(data.invoice.memo ?? '');
        setLines(
          data.lines.map(
            (l): InvoiceLineFormValue => ({
              item_id: l.item_id ?? '',
              description: l.description ?? '',
              quantity: l.quantity,
              unit_price: l.unit_price,
              tax_rate_id: l.tax_rate_id,
              account_id: l.account_id,
              warehouse_id: l.warehouse_id ?? '',
            }),
          ),
        );
        setSeeded(true);
      }
    },
  } as Parameters<typeof useQuery<{ invoice: InvoiceType; lines: InvoiceLine[] }>>[0]);

  const invoiceDetail = invoiceDetailQ.data?.invoice;

  // Live totals — percent×scale divisor (× 1_000_000 storage × 100 percent → fraction)
  const TAX_DIVISOR = 100_000_000n;
  let subtotalBig = 0n;
  let taxTotalBig = 0n;

  for (const l of lines) {
    try {
      if (l.quantity && l.unit_price) {
        const qty = BigInt(l.quantity);
        const price = BigInt(l.unit_price);
        const lineSub = (qty * price) / 1000n;
        subtotalBig += lineSub;

        const tr = taxRates.find((r) => r.id === l.tax_rate_id);
        if (tr) {
          taxTotalBig += (lineSub * BigInt(tr.percentage)) / TAX_DIVISOR;
        }
      }
    } catch {
      // ignore bad input
    }
  }
  const grandTotalBig = subtotalBig + taxTotalBig;

  const canSubmit =
    !!customerId &&
    !!entryDate &&
    !!dueDate &&
    effectiveCurrency.length === 3 &&
    lines.length >= 1 &&
    lines.every((l) => {
      if (!l.account_id || !l.unit_price) return false;
      const item = items.find((i) => i.id === l.item_id);
      if (item?.type === 'inventory' && !l.warehouse_id) return false;
      return true;
    });

  const submit = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        customer_id: customerId,
        entry_date: entryDate,
        due_date: dueDate,
        currency: effectiveCurrency,
        fx_rate: sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? fxRate),
        memo: memo || null,
        emission_point_id: emissionPointId ?? undefined,
        lines: lines.map((l, i) => ({
          line_number: i + 1,
          item_id: l.item_id || null,
          description: l.description || null,
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_rate_id: l.tax_rate_id || null,
          account_id: l.account_id,
          warehouse_id: l.warehouse_id || null,
        })),
      });
      return isNew
        ? api('/bookkeeping/sales/invoices', { method: 'POST', body })
        : api(`/bookkeeping/sales/invoices/${invoiceId}`, { method: 'PATCH', body });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-invoices'] });
      if (!isNew) await qc.invalidateQueries({ queryKey: ['bk-invoice', invoiceId] });
      nav('/app/bookkeeping/sales/invoices');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setSubmitError(err.body?.error ?? err.message ?? 'Error desconocido');
    },
  });

  return (
    <div className="space-y-4">
      {/* Title bar */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isNew
            ? t('sales.invoices.new')
            : t('sales.invoices.editTitle', { defaultValue: 'Editar factura' })}
        </h1>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/sales/invoices')}
          className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('journalEntries.cancel')}
        </button>
      </div>

      {/* Emission point picker */}
      <EmissionPointPicker value={emissionPointId} onChange={setEmissionPointId} />

      {/* Header fields */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {/* Customer */}
        <div className="col-span-2 md:col-span-1">
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.customer')}
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
              required
            >
              <option value="">— {t('sales.invoices.customer')}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Entry date */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.entry_date')}
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>

        {/* Due date */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.due_date')}
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.currency')}
            <input
              value={effectiveCurrency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm uppercase"
            />
          </label>
        </div>

        {/* FX rate */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('journalEntries.columns.fxRate')}
            <input
              value={sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? fxRate)}
              disabled={sameCurrency}
              onChange={(e) => setFxRate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            />
          </label>
        </div>

        {/* Memo */}
        <div className="col-span-2 md:col-span-3">
          <label className="block text-xs text-slate-500">
            {t('journalEntries.fields.memo')}
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              placeholder={t('journalEntries.fields.memoPlaceholder')}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
      </div>

      {/* Lines table */}
      <div className="overflow-x-auto rounded border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-2">{t('sales.line.item')}</th>
              <th className="px-2 py-2">{t('sales.line.description')}</th>
              <th className="px-2 py-2">{t('sales.line.quantity')}</th>
              <th className="px-2 py-2">{t('sales.line.unit_price')}</th>
              <th className="px-2 py-2">{t('sales.line.tax_rate')}</th>
              <th className="px-2 py-2">{t('sales.line.account')}</th>
              <th className="px-2 py-2">{t('inventory.line.warehouse')}</th>
              <th className="px-2 py-2 text-right">{t('sales.line.subtotal')}</th>
              <th className="px-2 py-2 text-right">{t('sales.line.tax_amount')}</th>
              <th className="px-2 py-2 text-right">{t('sales.line.total')}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <InvoiceLineRow
                // biome-ignore lint/suspicious/noArrayIndexKey: invoice lines are ordered form rows with no stable ID
                key={i}
                index={i}
                value={l}
                items={items}
                taxRates={taxRates}
                revenueAccounts={revenueAccounts}
                warehouses={warehouses}
                canRemove={lines.length > 1}
                onChange={(v) => {
                  setSubmitError(null);
                  setLines((ls) => ls.map((x, j) => (i === j ? v : x)));
                }}
                onRemove={() => setLines((ls) => ls.filter((_, j) => j !== i))}
              />
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={() => setLines((ls) => [...ls, defaultLine()])}
        className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
      >
        + {t('journalEntries.addLine')}
      </button>

      {/* Footer totals + submit */}
      <div className="rounded border bg-slate-50 px-4 py-3">
        <div className="flex flex-col items-end gap-1 text-sm">
          <div className="flex gap-4">
            <span className="text-slate-500">{t('sales.line.subtotal')}:</span>
            <span className="font-mono">
              {subtotalBig.toString()} {effectiveCurrency}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-slate-500">{t('sales.line.tax_amount')}:</span>
            <span className="font-mono">
              {taxTotalBig.toString()} {effectiveCurrency}
            </span>
          </div>
          <div className="flex gap-4 font-medium">
            <span className="text-slate-700">{t('sales.line.total')}:</span>
            <span className="font-mono">
              {grandTotalBig.toString()} {effectiveCurrency}
            </span>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => nav('/app/bookkeeping/sales/invoices')}
            className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            {t('journalEntries.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSubmit || submit.isPending}
            onClick={() => {
              setSubmitError(null);
              submit.mutate();
            }}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {submit.isPending ? '…' : t('journalEntries.submit')}
          </button>
        </div>
      </div>

      {submitError && <p className="text-sm text-red-600">{submitError}</p>}

      {/* SRI delivery block — only for saved invoices with clave_acceso */}
      {!isNew && invoiceDetail?.clave_acceso && (
        <SriStatusTrack docType="invoices" docId={invoiceDetail.id} />
      )}
    </div>
  );
}
